export interface Session {
  email: string;
  name: string | null;
  created_at: number;
}

const KEY = 'rlpscout:session:v1';
const INVITES_KEY = 'rlpscout:invites:v1';

export const OWNER_EMAIL = 'georgemogga1@gmail.com';

export class AccessDeniedError extends Error {
  constructor(email: string) {
    super(
      `${email} isn’t on the invite list. Ask the workspace owner (${OWNER_EMAIL}) to invite you.`
    );
    this.name = 'AccessDeniedError';
  }
}

function normalize(email: string): string {
  return email.trim().toLowerCase();
}

export function getInvites(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(INVITES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((x): x is string => typeof x === 'string').map(normalize);
  } catch {
    return [];
  }
}

function saveInvites(list: string[]): void {
  const deduped = Array.from(new Set(list.map(normalize))).filter(
    (e) => e && e !== OWNER_EMAIL
  );
  window.localStorage.setItem(INVITES_KEY, JSON.stringify(deduped));
}

export function addInvite(email: string): string[] {
  const next = [...getInvites(), normalize(email)];
  saveInvites(next);
  return getInvites();
}

export function removeInvite(email: string): string[] {
  const target = normalize(email);
  saveInvites(getInvites().filter((e) => e !== target));
  return getInvites();
}

export function isAllowed(email: string): boolean {
  const e = normalize(email);
  if (e === OWNER_EMAIL) return true;
  return getInvites().includes(e);
}

export function isOwner(email: string | null | undefined): boolean {
  return !!email && normalize(email) === OWNER_EMAIL;
}

export function getSession(): Session | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Session;
    if (!parsed.email) return null;
    if (!isAllowed(parsed.email)) {
      window.localStorage.removeItem(KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function signIn(email: string, _password: string): Session {
  const normalized = normalize(email);
  if (!isAllowed(normalized)) throw new AccessDeniedError(normalized);
  const next: Session = {
    email: normalized,
    name: null,
    created_at: Date.now(),
  };
  window.localStorage.setItem(KEY, JSON.stringify(next));
  return next;
}

export function signUp(name: string, email: string, _password: string): Session {
  const normalized = normalize(email);
  if (!isAllowed(normalized)) throw new AccessDeniedError(normalized);
  const next: Session = {
    email: normalized,
    name: name.trim() || null,
    created_at: Date.now(),
  };
  window.localStorage.setItem(KEY, JSON.stringify(next));
  return next;
}

export function signOut(): void {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(KEY);
}

export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}
